import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import duration from 'dayjs/plugin/duration'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import minMax from 'dayjs/plugin/minMax'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(duration)
dayjs.extend(minMax)

// Media spend and gross profit are stored on the product row, but fall back to
// deriving them from price + margin so rows saved before those fields existed
// (or rows mid-edit) still report a number instead of NaN.
const productMediaSpend = product => {
  const stored = parseFloat(product.mediaSpend)
  if (Number.isFinite(stored)) return stored
  const price = parseFloat(product.price) || 0
  const margin = parseFloat(product.margin) || 0
  return price - price * (margin / 100)
}

const productGrossProfit = product => {
  const stored = parseFloat(product.grossProfit)
  if (Number.isFinite(stored)) return stored
  return (parseFloat(product.price) || 0) - productMediaSpend(product)
}

// Gross margin as a percentage of revenue. Media spend is price minus gross
// profit by definition, so the two sum back to the price and there is no need
// to re-read price off the rows.
const marginPercent = (grossProfit, mediaSpend) => {
  const revenue = grossProfit + mediaSpend
  if (revenue <= 0) return 0
  return (grossProfit / revenue) * 100
}

export const calculatePaymentSchedule = (products, implementationFee = 0) => {
  if (!products || !products?.length) return
  const paymentSchedule = []

  // Ensure products is an array, or default to an empty array
  const validProducts = Array.isArray(products) ? products.filter(product => product.startDate && product.endDate) : []

  if (validProducts.length === 0) {
    return [] // Return an empty schedule if no valid products exist
  }

  // Determine the earliest start date and latest end date among all valid products
  const start = dayjs.min(validProducts.map(p => dayjs(p.startDate)))
  const end = dayjs.max(validProducts.map(p => dayjs(p.endDate)))

  // Iterate over each month between the start and end date
  for (let current = start.startOf('month'); current.isSameOrBefore(end); current = current.add(1, 'month')) {
    const month = current.format('MM')
    const year = current.format('YYYY')
    let paymentAmount = 0
    let grossProfit = 0
    let mediaSpend = 0
    let productCount = 0
    let activeDays = 0
    let productsAssociated = []

    validProducts.forEach(product => {
      const productStart = dayjs(product.startDate)
      const productEnd = dayjs(product.endDate)

      // Check if the product is active in the current month
      if (productStart.isSameOrBefore(current.endOf('month')) && productEnd.isSameOrAfter(current.startOf('month'))) {
        productCount += 1
        productsAssociated.push(product.productName)
        // Calculate the overlap days
        const overlapStart = productStart.isBefore(current.startOf('month')) ? current.startOf('month') : productStart
        const overlapEnd = productEnd.isAfter(current.endOf('month')) ? current.endOf('month') : productEnd
        activeDays += overlapEnd.diff(overlapStart, 'days') + 1
        // Set price to 0 if the payer is 'Client', otherwise use the product's price
        const productPrice =
          (product.payer === 'Client' && product.margin === 0) ||
          (product.payer === 'Client' && product.margin === '0.00')
            ? 0
            : product.payer === 'Client' && product.margin > 0
            ? parseFloat(product.grossProfit)
            : parseFloat(product.price)

        // Add product price to the payment amount
        paymentAmount += productPrice
        // Gross profit and media spend are reported for every active product
        // regardless of payer: the client-paid case simply bills the gross
        // profit and leaves the media spend to be paid direct.
        grossProfit += productGrossProfit(product)
        mediaSpend += productMediaSpend(product)
      }
    })

    paymentSchedule.push({
      month,
      year,
      paymentAmount: paymentAmount.toFixed(2), // Convert to 2 decimal places
      grossProfit: grossProfit.toFixed(2),
      mediaSpend: mediaSpend.toFixed(2),
      grossMargin: marginPercent(grossProfit, mediaSpend).toFixed(2),
      productCount,
      activeDays,
      productsAssociated: productsAssociated.join(', '),
    })
  }

  // A one-time implementation fee is billed up front — fold it into the
  // first scheduled month so the schedule (and its contract-total footer)
  // reflect the real first invoice. It is intentionally NOT spread across
  // months and NOT part of the recurring monthly figure.
  const fee = Number(implementationFee) || 0
  if (fee > 0 && paymentSchedule.length > 0) {
    const first = paymentSchedule[0]
    first.paymentAmount = (parseFloat(first.paymentAmount) + fee).toFixed(2)
    // The fee carries no media cost, so all of it lands in gross profit —
    // same treatment as calculateDealTotals.
    first.grossProfit = (parseFloat(first.grossProfit) + fee).toFixed(2)
    first.grossMargin = marginPercent(parseFloat(first.grossProfit), parseFloat(first.mediaSpend)).toFixed(2)
    first.productsAssociated = first.productsAssociated
      ? `${first.productsAssociated}, Implementation Fee`
      : 'Implementation Fee'
  }

  return paymentSchedule
}

export interface MonthlyScheduleSummary {
  /** True when the recurring monthly amount is not constant across months. */
  isFlighted: boolean
  /** Smallest non-zero monthly amount across the term. */
  min: number
  /** Largest monthly amount across the term. */
  max: number
  /** Count of months with a non-zero amount. */
  months: number
}

/**
 * Summarize the recurring monthly amounts of a product set so callers can
 * decide whether a single "monthly total" is meaningful. A schedule is
 * "flighted" when services start/stop or ramp at different times, so the
 * per-month amount varies — in that case a lone monthly figure is
 * misleading and the UI should defer to the payment schedule.
 *
 * Deliberately computed WITHOUT the implementation fee: a one-time first-
 * month charge must not be mistaken for a varying recurring monthly.
 */
export const getMonthlyScheduleSummary = (
  products: readonly unknown[] | undefined | null,
): MonthlyScheduleSummary => {
  const schedule = calculatePaymentSchedule(products) ?? []
  const amounts = (schedule as Array<{ paymentAmount: number | string }>)
    .map(r => Number(r.paymentAmount) || 0)
    .filter(a => a > 0)
  if (amounts.length === 0) return { isFlighted: false, min: 0, max: 0, months: 0 }
  const min = Math.min(...amounts)
  const max = Math.max(...amounts)
  // Penny tolerance so float noise doesn't read as flighted.
  return { isFlighted: max - min > 0.01, min, max, months: amounts.length }
}

// import dayjs from 'dayjs'
// import utc from 'dayjs/plugin/utc'
// import timezone from 'dayjs/plugin/timezone'
// import advancedFormat from 'dayjs/plugin/advancedFormat'
// import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
// import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
// import duration from 'dayjs/plugin/duration'
// import minMax from 'dayjs/plugin/minMax'

// dayjs.extend(utc)
// dayjs.extend(timezone)
// dayjs.extend(advancedFormat)
// dayjs.extend(isSameOrBefore)
// dayjs.extend(isSameOrAfter)
// dayjs.extend(duration)
// dayjs.extend(minMax)

// export const calculatePaymentSchedule = deal => {
//   const paymentSchedule = []
//   const { products } = deal || {} // Ensure deal and products are defined

//   // Ensure products is an array, or default to an empty array
//   const validProducts = Array.isArray(products) ? products.filter(product => product.startDate && product.endDate) : []

//   if (validProducts.length === 0) {
//     return [] // Return an empty schedule if no valid products exist
//   }

//   // Determine the earliest start date and latest end date among all valid products
//   const start = dayjs.min(validProducts.map(p => dayjs(p.startDate)))
//   const end = dayjs.max(validProducts.map(p => dayjs(p.endDate)))

//   // Iterate over each month between the start and end date
//   for (let current = start.startOf('month'); current.isSameOrBefore(end); current = current.add(1, 'month')) {
//     const month = current.format('MM')
//     const year = current.format('YYYY')
//     let paymentAmount = 0
//     let productCount = 0
//     let activeDays = 0
//     let productsAssociated = []

//     validProducts.forEach(product => {
//       const productStart = dayjs(product.startDate)
//       const productEnd = dayjs(product.endDate)

//       // Check if the product is active in the current month
//       if (productStart.isSameOrBefore(current.endOf('month')) && productEnd.isSameOrAfter(current.startOf('month'))) {
//         productCount += 1
//         productsAssociated.push(product.productName)
//         // Calculate the overlap days
//         const overlapStart = productStart.isBefore(current.startOf('month')) ? current.startOf('month') : productStart
//         const overlapEnd = productEnd.isAfter(current.endOf('month')) ? current.endOf('month') : productEnd
//         activeDays += overlapEnd.diff(overlapStart, 'days') + 1
//         // Set price to 0 if the payer is 'Client', otherwise use the product's price
//         const productPrice =
//           product.payer === 'Client' && product.margin === 0
//             ? 0
//             : product.payer === 'Client' && product.margin > 0
//             ? parseFloat(product.grossProfit)
//             : parseFloat(product.price)

//         // Add product price to the payment amount
//         paymentAmount += productPrice
//       }
//     })
//     console.log('activeDays', activeDays)

//     paymentSchedule.push({
//       month,
//       year,
//       paymentAmount: paymentAmount.toFixed(2), // Convert to 2 decimal places
//       productCount,
//       activeDays,
//       productsAssociated: productsAssociated.join(', '),
//     })
//   }

//   return paymentSchedule
// }
